import React from "react";
import { Link } from "react-router-dom";
import { Lock, ShieldAlert } from "lucide-react";
import { addDays, barClass, hhmm, parseLocal, toneClass } from "./helpers";

const HOURS = [0, 3, 6, 9, 12, 15, 18, 21];

const Bar = ({ ev, dayStart, dayEnd }) => {
  const span = dayEnd - dayStart;
  const s = Math.max(ev.start, dayStart);
  const e = Math.min(ev.end, dayEnd);
  const left = ((s - dayStart) / span) * 100;
  const width = Math.max(1.5, ((e - s) / span) * 100);
  return (
    <div className={`absolute top-1 bottom-1 rounded ${barClass(ev.tone)} ${ev.kind === "hold" ? "border border-dashed border-violet-700" : ""}`}
      style={{ left: `${left}%`, width: `${width}%` }}
      title={`${ev.title} · ${hhmm(ev.start)}–${hhmm(ev.end)}`}
      data-testid={`cal-bar-${ev.key}`}>
      <span className="block truncate px-1.5 text-[10px] font-semibold leading-5 text-white/95">
        {hhmm(ev.start)} {ev.kind === "booking" ? (ev.raw.customer_name || "") : (ev.raw.title || ev.raw.reason)}
      </span>
    </div>
  );
};

export const DayView = ({ dayIso, events, units }) => {
  const dayStart = parseLocal(`${dayIso}T00:00:00`);
  const dayEnd = addDays(dayStart, 1);
  const forUnit = (uid) => events.filter((e) => (e.units || []).includes(uid));
  const unassigned = events.filter((e) => !(e.units || []).length);

  return (
    <div data-testid="calendar-day-view">
      <div className="-mx-1 overflow-x-auto pb-2">
        <div className="min-w-[760px] px-1">
          <div className="mb-1 flex pl-[124px]">
            {HOURS.map((h) => (
              <div key={h} className="flex-1 text-[10px] uppercase tracking-widest text-ink-soft">
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            {units.map((u) => {
              const items = forUnit(u.id);
              return (
                <div key={u.id} className="flex items-stretch gap-2" data-testid={`cal-unit-row-${u.unit_code || u.id}`}>
                  <div className="w-[116px] shrink-0 rounded-md border border-black/10 bg-white px-2 py-1.5">
                    <div className="truncate text-[11px] font-semibold">{u.unit_code || u.id}</div>
                    <div className="truncate text-[10px] text-ink-soft">{u.status}</div>
                  </div>
                  <div className="relative h-[38px] flex-1 rounded-md border border-black/10 bg-cream-dark/50">
                    {HOURS.map((h, i) => (
                      <div key={h} className="absolute top-0 bottom-0 border-l border-black/5"
                        style={{ left: `${(i / HOURS.length) * 100}%` }} />
                    ))}
                    {items.map((e) => <Bar key={e.key} ev={e} dayStart={dayStart} dayEnd={dayEnd} />)}
                    {items.length === 0 && (
                      <span className="absolute inset-0 flex items-center pl-2 text-[10px] text-ink-soft">Bebas</span>
                    )}
                  </div>
                </div>
              );
            })}
            {units.length === 0 && (
              <div className="rounded-md border border-dashed border-black/15 p-6 text-center text-sm text-ink-soft">
                Belum ada unit di Hub ini.
              </div>
            )}
          </div>
        </div>
      </div>

      {unassigned.length > 0 && (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="cal-unassigned">
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-900">
            <ShieldAlert size={14} /> Belum ada meja yang ditetapkan
          </div>
          <div className="mt-2 space-y-1.5">
            {unassigned.map((e) => {
              const body = <span className="text-[11px]">{e.title} · {e.sub}</span>;
              return e.link
                ? <Link key={e.key} to={e.link} className={`block rounded border px-2 py-1 ${toneClass(e.tone)}`}>{body}</Link>
                : <div key={e.key} className={`rounded border px-2 py-1 ${toneClass(e.tone)}`}>{body}</div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const LockList = ({ locks, onRelease, onConvert, canEdit }) => (
  <div className="mt-6" data-testid="lock-list">
    <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg">
      <Lock size={15} /> Kunci kalender aktif
    </h2>
    <p className="mt-1 text-xs text-ink-soft">
      Hold & blokir mengunci meja persis, tapi tidak pernah masuk pendapatan, komisi, atau poin loyalty.
    </p>
    <div className="mt-3 space-y-2">
      {locks.map((l) => (
        <div key={l.id} data-testid={`lock-row-${l.id}`}
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-black/10 bg-white p-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full border px-2 py-0.5 text-[10px] ${l.lock_type === "admin_hold"
                ? "border-violet-400 bg-violet-50 text-violet-800" : "border-zinc-400 bg-zinc-100 text-zinc-700"}`}>
                {l.lock_type_label}
              </span>
              <span className="truncate text-sm font-semibold">{l.title}</span>
            </div>
            <div className="mt-1 text-[11px] text-ink-soft">
              {l.branch_name} · {l.rental_date} {l.all_day ? "sehari penuh" : `${l.start_time}–${l.end_time}`} ·{" "}
              {l.quantity} meja {(l.unit_codes || []).length ? `(${l.unit_codes.join(", ")})` : ""}
              {l.expires_at ? ` · kedaluwarsa ${new Date(l.expires_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
            </div>
            {l.notes && <div className="mt-1 text-[11px] text-ink-soft">{l.notes}</div>}
          </div>
          {canEdit && (
            <div className="flex gap-2">
              {l.lock_type === "admin_hold" && (
                <button data-testid={`lock-convert-${l.id}`} onClick={() => onConvert(l)}
                  className="rounded-md border border-ink bg-ink px-3 py-1.5 text-xs text-cream hover:opacity-90">
                  Jadikan booking
                </button>
              )}
              <button data-testid={`lock-release-${l.id}`} onClick={() => onRelease(l)}
                className="rounded-md border border-black/15 px-3 py-1.5 text-xs hover:border-lm-red hover:text-lm-red">
                Lepas kunci
              </button>
            </div>
          )}
        </div>
      ))}
      {locks.length === 0 && (
        <div className="rounded-lg border border-dashed border-black/15 p-5 text-center text-sm text-ink-soft">
          Tidak ada hold atau blokir aktif di rentang ini.
        </div>
      )}
    </div>
  </div>
);
